export const translations = {
  en: {
    brand: 'B-Code',
    nav: {
      backToTop: 'Back to top',
      services: 'Services',
      portfolio: 'Portfolio',
      pricing: 'Pricing',
      about: 'About',
      blog: 'Blog',
      contact: 'Contact',
    },
    cta: {
      quote: 'Get a quote',
      start: "Let's build yours",
      explore: 'Explore services',
      consult: 'Book a free consult',
      seePricing: 'See pricing',
      choose: 'Choose plan',
      maintenance: 'Get maintenance',
      send: 'Send message',
      learnMore: 'Learn more',
      viewProject: 'View project',
      startYours: 'Start yours',
      requestQuote: 'Request a quote',
      tailored: 'Request a tailored quote',
      tryAgain: 'Try again',
    },
    hero: {
      brand: 'B-Code',
      line1: 'We build software',
      line2: 'that grows your business.',
      lead:
        'Web platforms, Odoo ERP, and custom software — engineered for companies that need reliable technology and clear results.',
      cta: 'Start your project',
    },
    manifesto: {
      kicker: 'The craft',
      lines: [
        'We turn quiet ambition into **working** systems',
        'Websites that **convert**, Odoo that **runs**, software that **fits**',
        'No noise — only **clarity**, speed, and craft',
        'From first sketch to final **ship**, we stay close',
        'Build once, **scale** with confidence',
        'From idea to **launch**. Real results.',
      ],
    },
    services: {
      kicker: 'What we build',
      title: 'Fueling your digital journey',
      lead: 'Three focused pillars — websites that convert, Odoo systems that run operations, and software built around your process.',
      webTitle: 'Web Development',
      webDesc:
        'Fast, conversion-focused websites and web apps — from marketing sites to full product platforms.',
      webPoints: ['React & modern frontends', 'APIs & dashboards', 'Performance & SEO'],
      webCta: 'Discuss a website',
      odooTitle: 'Odoo Development',
      odooDesc:
        'Odoo ERP setup, customization, and integrations that fit how your company actually operates.',
      odooPoints: ['Implementation & modules', 'Custom workflows', 'Integrations & training'],
      odooCta: 'Plan an Odoo project',
      softTitle: 'Custom Software',
      softDesc:
        'Internal tools, automation, and business systems built around your processes — not the other way around.',
      softPoints: ['Business automation', 'Internal platforms', 'Ongoing product support'],
      softCta: 'Build custom software',
      unsureTitle: 'Not sure where to start?',
      unsureLead: "Tell us your goal — we'll recommend the right stack and a clear next step.",
    },
    industries: {
      kicker: 'Industries',
      title: 'Solutions for every industry',
      lead: 'We adapt the same engineering craft to the realities of your market.',
      items: [
        'Real Estate',
        'Logistics',
        'E-commerce',
        'Education',
        'Healthcare',
        'Finance',
        'Manufacturing',
        'Services',
      ],
    },
    work: {
      kicker: 'Portfolio',
      title: 'Recent launches',
      lead: 'A sample of websites, Odoo builds, and software shipped for real teams.',
      viewAll: 'View all projects',
      viewAllCount: 'View all {n} projects',
      allTitle: 'All projects',
      allLead: 'Websites, Odoo systems and software products we have designed and shipped.',
      filterAll: 'All',
    },
    servicePage: {
      cta: 'Start your project',
      seeWork: 'See related work',
      includes: "What's included",
      related: 'Related projects',
      other: 'Other services',
      learnMore: 'Learn more',
    },
    project: {
      back: 'Back to portfolio',
      visitLive: 'Visit live site',
      client: 'Client',
      year: 'Year',
      role: 'Role',
      overview: 'Overview',
      challenge: 'Challenge',
      solution: 'Solution',
      results: 'Results',
      stack: 'Stack',
      industry: 'Industry',
      caseStudy: 'Case study',
      nextProject: 'Next project',
      galleryKicker: 'Responsive by design',
      galleryTitle: 'Built for every screen',
      galleryLead: 'Real screenshots from the live site — desktop, tablet, and mobile.',
      desktop: 'Desktop',
      mobile: 'Mobile',
      prevScreen: 'Previous screen',
      nextScreen: 'Next screen',
      swipeHint: 'Swipe to see more screens',
      ctaTitle: 'Have a similar project in mind?',
      ctaLead: "Tell us what you need — we'll reply with a clear plan and a quote.",
      ctaSimilar: 'Start a similar project',
      getDemo: 'Book a demo',
      compatibility: 'Compatibility',
      compatibleWith: 'Compatible with',
      odooVersions: 'Odoo versions',
      editions: { enterprise: 'Enterprise', community: 'Community', 'odoo-sh': 'Odoo.sh', online: 'Odoo Online' },
      featuresKicker: 'Features',
      featuresTitle: 'Everything it does',
      featuresLead: 'Each feature below is shown on a real screen from the app.',
      screensKicker: 'Screens',
      screensTitle: 'Every screen in the app',
      screensLead: 'Real screenshots of each menu and view — select one to enlarge it.',
      closeImage: 'Close image',
      demoTitle: 'See it in action',
      demoLead: "Book a live demo and we'll take you through every screen and answer all your questions.",
    },
    pricing: {
      kicker: 'Pricing',
      title: 'Transparent packages',
      lead: 'Start with a package that matches your scope — or talk to us for a custom Odoo / software quote.',
      website: 'Website packages',
      maintenance: 'Maintenance plans',
      delivery: 'Delivery',
      mostChosen: 'Most chosen',
      loading: 'Loading pricing...',
      error: 'Failed to load pricing',
      odooNote: 'Need Odoo ERP or custom software?',
    },
    process: {
      kicker: 'Our approach',
      title: 'From idea to launch',
      lead: 'No vague timelines. You always know what happens next.',
      steps: [
        {
          step: '01',
          title: 'Discover',
          text: 'We clarify goals, users, and constraints — so the build starts with the right problem.',
        },
        {
          step: '02',
          title: 'Design',
          text: 'Scope, UX, and technical approach aligned to budget and timeline before we write code.',
        },
        {
          step: '03',
          title: 'Deploy',
          text: 'Iterative delivery with previews, testing, and a clean handoff your team can own.',
        },
      ],
      bandTitle: 'Ready to ship something that lasts?',
      bandLead: "Share your brief — we respond with a practical recommendation and next steps.",
    },
    tech: {
      kicker: 'Tech stack',
      title: 'Building with the best tools',
      lead: 'Modern, maintainable stacks — chosen for speed, reliability, and long-term ownership.',
      frontend: 'Front End',
      backend: 'Back End & Platforms',
      tools: 'Cloud & Tools',
    },
    about: {
      kicker: 'About B-Code',
      title: 'A technology partner for growing companies',
      p1: 'B-Code designs and builds digital systems that are clear, maintainable, and ready for real business use. We combine product thinking with solid engineering.',
      p2: 'Whether you need a high-performing web presence, an ERP that matches your operations, or a tailored internal tool — we focus on outcomes you can measure.',
      stats: [
        { value: '30+', label: 'Projects delivered' },
        { value: 'Web + Odoo', label: 'Core expertise' },
        { value: 'End-to-end', label: 'From idea to launch' },
      ],
    },
    testimonials: {
      kicker: 'Clients',
      title: 'What our clients say',
      items: [
        {
          quote: 'Professional delivery and clear communication from kickoff to launch.',
          name: 'Operations Lead',
          role: 'Education platform',
        },
        {
          quote: 'They turned a messy ERP need into a system our team actually uses daily.',
          name: 'Managing Director',
          role: 'Services company',
        },
        {
          quote: 'Fast website, sharp UX, and measurable lead improvement after go-live.',
          name: 'Marketing Manager',
          role: 'Digital agency client',
        },
      ],
    },
    contact: {
      demoMessage: "Hi, I'd like a demo of {name}.",
      kicker: 'Contact',
      title: 'Ready to start your project?',
      lead: 'Share a short brief — website, Odoo, or custom software. We’ll reply with next steps.',
      replyTime: 'Usually replies within 24 hours',
      email: 'Email',
      emailCta: 'Write us',
      focus: 'Focus',
      focusValue: 'Web · Odoo · Custom software',
      focusTags: ['Web', 'Odoo', 'Custom software'],
      formEyebrow: 'Project brief',
      name: 'Full name',
      phone: 'Phone number',
      whatsapp: 'WhatsApp number',
      whatsappSame: 'Use the same number for WhatsApp',
      workEmail: 'Work email',
      need: 'What do you need?',
      options: {
        web: 'Web development',
        odoo: 'Odoo development',
        software: 'Custom software',
        other: 'Other / not sure',
      },
      details: 'Project details',
      placeholderName: 'Your name',
      placeholderEmail: 'you@company.com',
      placeholderPhone: '1001234567',
      placeholderWhatsapp: '1001234567',
      placeholderMsg: 'Goals, timeline, and anything we should know...',
      searchCountry: 'Search country...',
      noCountry: 'No results',
      sending: 'Sending...',
      success: 'Thanks — we received your message and will reply soon.',
      error: 'Could not send. Please check the required fields and try again.',
    },
    blog: {
      kicker: 'Blog',
      title: 'Insights & updates',
      lead: 'Practical notes on websites, Odoo, and building software for growing companies.',
      latest: 'Latest from the blog',
      viewAll: 'View all articles',
      readMore: 'Read article',
      back: 'Back to blog',
      empty: 'No articles yet — check back soon.',
      minRead: 'min read',
      notFound: 'This article is not available.',
      faqTitle: 'Frequently asked questions',
      searchLabel: 'Search articles',
      searchPlaceholder: 'Search articles — e.g. Odoo, ZATCA, website cost',
      searchHint: 'Press / to search',
      allTopics: 'All topics',
      topics: 'Topics',
      sortBy: 'Sort by',
      sortNewest: 'Newest',
      sortOldest: 'Oldest',
      sortQuickest: 'Quick reads',
      featured: 'Featured',
      results: '{n} articles',
      result: '1 article',
      noResults: 'No articles match your search.',
      clearFilters: 'Clear search and filters',
      quickReads: 'Quick reads',
      latestArticles: 'Latest articles',
      sideCtaTitle: 'Planning a website or Odoo project?',
      sideCtaLead: 'Tell us what you need and get a clear plan and quote.',
      sideCtaButton: 'Talk to us',
      home: 'Home',
      share: 'Share',
      copyLink: 'Copy link',
      linkCopied: 'Link copied',
      moreArticles: 'More articles',
      related: 'Related articles',
      writtenBy: 'Written by',
      authorBio: 'B-Code builds websites, Odoo ERP systems and custom software for growing companies in Saudi Arabia.',
    },
    footer: {
      tagline: "Let's build the next system your business needs.",
      sitemap: 'Sitemap',
      connect: 'Connect',
      rights: 'Web · Odoo · Software.',
      contact: 'Contact',
      call: 'Call us',
      whatsapp: 'WhatsApp',
      madeBy: 'Made by',
    },
    a11y: {
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      toggleTheme: 'Toggle color theme',
      toggleLang: 'Toggle language',
    },
    faq: {
      kicker: 'FAQ',
      title: 'Answers before you ask',
      lead: 'Straight answers about what B-Code builds, how we work, and how to start.',
    },
    notFound: {
      title: 'Page not found',
      lead: 'That link does not exist. Head back home to explore services, work, and pricing.',
      home: 'Back to home',
    },
  },
  ar: {
    brand: 'B-Code',
    nav: {
      backToTop: 'العودة إلى الأعلى',
      services: 'الخدمات',
      portfolio: 'أعمالنا',
      pricing: 'الأسعار',
      about: 'من نحن',
      blog: 'المدونة',
      contact: 'تواصل',
    },
    cta: {
      quote: 'عرض سعر',
      start: 'لنبنِ مشروعك معنا',
      explore: 'تعرّف على خدماتنا',
      consult: 'احجز استشارة مجانية',
      seePricing: 'اطّلع على الأسعار',
      choose: 'اختر هذه الباقة',
      maintenance: 'اشترك في خطة الصيانة',
      send: 'أرسل الرسالة',
      learnMore: 'اعرف المزيد',
      viewProject: 'استعرض المشروع',
      startYours: 'ابدأ مشروعك',
      requestQuote: 'اطلب عرض سعر',
      tailored: 'اطلب عرضاً مخصصاً',
      tryAgain: 'إعادة المحاولة',
    },
    hero: {
      brand: 'B-Code',
      line1: 'نبني برمجيات',
      line2: 'تنمو معها أعمالك.',
      lead:
        'نبني مواقع إلكترونية وأنظمة أودو وحلولاً برمجية مخصصة، لنقود أعمالك بثقة ونحقّق نتائج ملموسة.',
      cta: 'ابدأ مشروعك الآن',
    },
    manifesto: {
      kicker: 'الحرفة',
      lines: [
        'نحوّل الطموح الهادئ إلى أنظمة **تعمل**',
        'مواقع **تحوّل**، أودو **يعمل**، برمجيات **تناسبك**',
        'بلا ضجيج — فقط **وضوح** وسرعة وحرفة',
        'من أول رسمة حتى **الإطلاق**، نبقى قريبين',
        'ابنِ مرة، و**توسّع** بثقة',
        'من الفكرة إلى **الإطلاق**. نتائج حقيقية.',
      ],
    },
    services: {
      kicker: 'خدماتنا',
      title: 'حلول رقمية تدفع نمو أعمالك',
      lead:
        'ثلاثة مسارات متخصصة: مواقع تحوّل الزوار إلى عملاء، أنظمة أودو تُنظّم عملياتك، وبرمجيات تُصمَّم وفق احتياجاتك.',
      webTitle: 'تطوير المواقع والتطبيقات',
      webDesc:
        'مواقع وتطبيقات ويب سريعة وعصرية، مصممة للوضوح والأداء وتحويل الاهتمام إلى فرص حقيقية.',
      webPoints: [
        'واجهات حديثة بتقنيات React',
        'لوحات تحكم وواجهات برمجية متكاملة',
        'أداء عالٍ وتحسين لمحركات البحث',
      ],
      webCta: 'ناقش مشروع موقعك معنا',
      odooTitle: 'تطوير أنظمة أودو',
      odooDesc:
        'تنفيذ وتخصيص وتكامل أنظمة أودو بما ينسجم مع أسلوب عملك الفعلي — لا قوالب جاهزة بلا فائدة.',
      odooPoints: [
        'تثبيت وتفعيل الوحدات المناسبة',
        'أتمتة سير العمل حسب عملياتك',
        'تكامل الأنظمة وتدريب الفريق',
      ],
      odooCta: 'خطّط لمشروع أودو',
      softTitle: 'البرمجيات المخصصة',
      softDesc:
        'أدوات داخلية وأنظمة أتمتة تُبنى حول عمليات شركتك، لتمنحك كفاءة أعلى وتحكماً أوضح.',
      softPoints: [
        'أتمتة العمليات التجارية',
        'منصات داخلية لإدارة العمل',
        'دعم وتطوير مستمر للمنتج',
      ],
      softCta: 'ابنِ حلولك معنا',
      unsureTitle: 'لست متأكداً من نقطة الانطلاق؟',
      unsureLead:
        'شاركنا هدفك، وسنقترح المسار التقني الأنسب مع خطوات عملية واضحة للبدء.',
    },
    industries: {
      kicker: 'القطاعات',
      title: 'خبرات تناسب مختلف الصناعات',
      lead: 'نُكيّف حلولنا التقنية لتلائم طبيعة سوقك ومتطلبات عملك.',
      items: [
        'العقارات',
        'اللوجستيات',
        'التجارة الإلكترونية',
        'التعليم',
        'الرعاية الصحية',
        'الخدمات المالية',
        'التصنيع',
        'خدمات الأعمال',
      ],
    },
    work: {
      kicker: 'أعمالنا',
      title: 'مشاريع نفخر بإنجازها',
      lead: 'نماذج مختارة من مواقع وأنظمة أودو وحلول برمجية سلّمناها لشركاء حقيقيين.',
      viewAll: 'عرض جميع المشاريع',
      viewAllCount: 'عرض جميع المشاريع ({n})',
      allTitle: 'جميع المشاريع',
      allLead: 'مواقع وأنظمة أودو ومنتجات برمجية صممناها وأطلقناها.',
      filterAll: 'الكل',
    },
    servicePage: {
      cta: 'ابدأ مشروعك',
      seeWork: 'شاهد أعمالنا',
      includes: 'ماذا تتضمن الخدمة',
      related: 'مشاريع ذات صلة',
      other: 'خدمات أخرى',
      learnMore: 'اعرف المزيد',
    },
    project: {
      back: 'العودة إلى أعمالنا',
      visitLive: 'زيارة الموقع الحي',
      client: 'العميل',
      year: 'سنة الإنجاز',
      role: 'نطاق العمل',
      overview: 'نبذة عن المشروع',
      challenge: 'التحدي',
      solution: 'الحل',
      results: 'النتائج',
      stack: 'التقنيات المستخدمة',
      industry: 'القطاع',
      caseStudy: 'دراسة حالة',
      nextProject: 'المشروع التالي',
      galleryKicker: 'متجاوب بالكامل',
      galleryTitle: 'مصمّم لكل الشاشات',
      galleryLead: 'لقطات حقيقية من الموقع المباشر — على سطح المكتب والجهاز اللوحي والجوال.',
      desktop: 'سطح المكتب',
      mobile: 'الجوال',
      prevScreen: 'الشاشة السابقة',
      nextScreen: 'الشاشة التالية',
      swipeHint: 'اسحب لمشاهدة المزيد من الشاشات',
      ctaTitle: 'لديك مشروع مشابه؟',
      ctaLead: 'أخبرنا بما تحتاجه، وسنعود إليك بخطة واضحة وعرض سعر.',
      ctaSimilar: 'ابدأ مشروعاً مماثلاً',
      getDemo: 'احجز عرضاً تجريبياً',
      compatibility: 'التوافق',
      compatibleWith: 'متوافق مع',
      odooVersions: 'إصدارات أودو',
      editions: { enterprise: 'إنتربرايز', community: 'كوميونيتي', 'odoo-sh': 'Odoo.sh', online: 'أودو أونلاين' },
      featuresKicker: 'المزايا',
      featuresTitle: 'كل ما يقدّمه التطبيق',
      featuresLead: 'كل ميزة معروضة على شاشة حقيقية من التطبيق.',
      screensKicker: 'الشاشات',
      screensTitle: 'جميع شاشات التطبيق',
      screensLead: 'لقطات حقيقية لكل قائمة وعرض — اضغط على أي لقطة لتكبيرها.',
      closeImage: 'إغلاق الصورة',
      demoTitle: 'شاهد التطبيق مباشرةً',
      demoLead: 'احجز عرضاً تجريبياً، وسنأخذك في جولة على كل شاشات التطبيق ونجيب عن جميع أسئلتك.',
    },
    pricing: {
      kicker: 'الأسعار',
      title: 'باقات شفافة تناسب أهدافك',
      lead:
        'اختر الباقة الأنسب لنطاق مشروعك، أو تواصل معنا لعرض مخصص لأنظمة أودو والحلول البرمجية.',
      website: 'باقات تطوير المواقع',
      maintenance: 'خطط الصيانة والدعم',
      delivery: 'مدة التسليم',
      mostChosen: 'الأكثر طلباً',
      loading: 'جاري تحميل الأسعار...',
      error: 'تعذّر تحميل بيانات الأسعار',
      odooNote: 'هل تحتاج نظام أودو أو حلاً برمجياً مخصصاً؟',
    },
    process: {
      kicker: 'منهجية العمل',
      title: 'من الفكرة إلى الإطلاق بوضوح تام',
      lead: 'خطة عمل شفافة في كل مرحلة — تعرف دائماً أين نقف وما الخطوة التالية.',
      steps: [
        {
          step: '01',
          title: 'الاستكشاف',
          text: 'نحدد الأهداف والمستخدمين والقيود بدقة، ليبدأ التنفيذ من فهم صحيح للمشكلة.',
        },
        {
          step: '02',
          title: 'التصميم والتخطيط',
          text: 'نضع نطاق العمل وتجربة الاستخدام والخطة التقنية بما يتوافق مع ميزانيتك وجدولك الزمني.',
        },
        {
          step: '03',
          title: 'البناء والإطلاق',
          text: 'تسليم مرحلي مع معاينات واختبارات، ثم تسليم منظم يمكّن فريقك من إدارة الحل بثقة.',
        },
      ],
      bandTitle: 'هل أنت مستعد لإطلاق حل يدوم؟',
      bandLead: 'أرسل موجزاً قصيراً عن مشروعك، وسنعود إليك بتوصية عملية وخطوات واضحة.',
    },
    tech: {
      kicker: 'ترسانتنا التقنية',
      title: 'نبني بأدوات موثوقة وحديثة',
      lead:
        'نختار التقنيات بعناية لضمان السرعة والاستقرار وسهولة الصيانة على المدى الطويل.',
      frontend: 'الواجهات الأمامية',
      backend: 'الخوادم والمنصات',
      tools: 'السحابة والأدوات',
    },
    about: {
      kicker: 'عن B-Code',
      title: 'شريكك التقني لنموّ أعمالك',
      p1: 'في B-Code نصمّم ونبني أنظمة رقمية واضحة وقابلة للتطوير وجاهزة للاستخدام اليومي. نجمع بين التفكير المنتج والهندسة الدقيقة لنقدّم حلولاً تخدم أعمالك فعلياً.',
      p2: 'سواء احتجت حضوراً رقمياً احترافياً، أو نظام ERP يواكب عملياتك، أو أداة داخلية مخصصة — نركّز على نتائج قابلة للقياس وأثر ملموس على أعمالك.',
      stats: [
        { value: '+30', label: 'مشروعاً منجَزاً' },
        { value: 'ويب + أودو', label: 'تخصصنا الأساسي' },
        { value: 'من البداية للنهاية', label: 'شراكة متكاملة' },
      ],
    },
    testimonials: {
      kicker: 'آراء عملائنا',
      title: 'ثقة تُبنى بالنتائج',
      items: [
        {
          quote:
            'التزام احترافي وتواصل واضح من أول اجتماع حتى يوم الإطلاق. تجربة سلسة ومطمئنة.',
          name: 'مسؤول العمليات',
          role: 'منصة تعليمية',
        },
        {
          quote:
            'حوّلوا احتياجنا المعقد إلى نظام ERP يعتمده الفريق يومياً بكفاءة عالية.',
          name: 'المدير العام',
          role: 'شركة خدمات',
        },
        {
          quote:
            'موقع سريع وتجربة استخدام راقية، مع تحسّن واضح في جودة العملاء المحتملين بعد الإطلاق.',
          name: 'مدير التسويق',
          role: 'عميل في القطاع الرقمي',
        },
      ],
    },
    contact: {
      demoMessage: 'مرحباً، أرغب في عرض تجريبي لـ {name}.',
      kicker: 'تواصل معنا',
      title: 'لنبدأ مشروعك القادم معاً',
      lead:
        'شاركنا موجزاً عن احتياجك — موقعاً، نظام أودو، أو حلاً برمجياً — وسنرد عليك بالخطوات التالية.',
      replyTime: 'نرد عادة خلال 24 ساعة',
      email: 'البريد الإلكتروني',
      emailCta: 'راسلنا',
      focus: 'مجالات تخصصنا',
      focusValue: 'تطوير الويب · أنظمة أودو · البرمجيات المخصصة',
      focusTags: ['الويب', 'أودو', 'برمجيات مخصصة'],
      formEyebrow: 'موجز المشروع',
      name: 'الاسم الكامل',
      phone: 'رقم الهاتف',
      whatsapp: 'رقم واتساب',
      whatsappSame: 'استخدم نفس الرقم لواتساب',
      workEmail: 'البريد المهني',
      need: 'ما الذي تبحث عنه؟',
      options: {
        web: 'تطوير موقع أو تطبيق ويب',
        odoo: 'تطوير أو تخصيص نظام أودو',
        software: 'حل برمجي مخصص',
        other: 'أخرى / أرغب في الاستشارة',
      },
      details: 'تفاصيل المشروع',
      placeholderName: 'الاسم الكامل',
      placeholderEmail: 'you@company.com',
      placeholderPhone: '1001234567',
      placeholderWhatsapp: '1001234567',
      placeholderMsg: 'صف أهدافك، الجدول الزمني المتوقع، وأي تفاصيل تساعدنا على فهم احتياجك...',
      searchCountry: 'ابحث عن دولة...',
      noCountry: 'لا نتائج',
      sending: 'جاري الإرسال...',
      success: 'شكراً — استلمنا رسالتك وسنرد عليك قريباً.',
      error: 'تعذّر الإرسال. تحقق من الحقول المطلوبة وحاول مرة أخرى.',
    },
    blog: {
      kicker: 'المدونة',
      title: 'مقالات وتحديثات',
      lead: 'أفكار عملية حول المواقع الإلكترونية وأودو وبناء البرمجيات للشركات النامية.',
      latest: 'أحدث المقالات',
      viewAll: 'عرض جميع المقالات',
      readMore: 'اقرأ المقال',
      back: 'العودة إلى المدونة',
      empty: 'لا توجد مقالات بعد — تابعنا قريباً.',
      minRead: 'دقائق قراءة',
      notFound: 'هذا المقال غير متاح.',
      faqTitle: 'أسئلة شائعة',
      searchLabel: 'ابحث في المقالات',
      searchPlaceholder: 'ابحث في المقالات — مثل: أودو، زاتكا، تكلفة الموقع',
      searchHint: 'اضغط / للبحث',
      allTopics: 'كل المواضيع',
      topics: 'المواضيع',
      sortBy: 'الترتيب',
      sortNewest: 'الأحدث',
      sortOldest: 'الأقدم',
      sortQuickest: 'قراءة سريعة',
      featured: 'مقال مميز',
      results: '{n} مقالات',
      result: 'مقال واحد',
      noResults: 'لا توجد مقالات تطابق بحثك.',
      clearFilters: 'مسح البحث والفلاتر',
      quickReads: 'قراءات سريعة',
      latestArticles: 'أحدث المقالات',
      sideCtaTitle: 'تخطط لموقع أو مشروع أودو؟',
      sideCtaLead: 'أخبرنا بما تحتاجه واحصل على خطة واضحة وعرض سعر.',
      sideCtaButton: 'تحدث معنا',
      home: 'الرئيسية',
      share: 'مشاركة',
      copyLink: 'نسخ الرابط',
      linkCopied: 'تم نسخ الرابط',
      moreArticles: 'مقالات أخرى',
      related: 'مقالات ذات صلة',
      writtenBy: 'بقلم',
      authorBio: 'تبني B-Code المواقع الإلكترونية وأنظمة أودو والبرمجيات المخصصة للشركات النامية في السعودية.',
    },
    footer: {
      tagline: 'لنبنِ معاً النظام الذي تستحقه أعمالك.',
      sitemap: 'روابط سريعة',
      connect: 'تواصل',
      rights: 'تطوير الويب · أنظمة أودو · البرمجيات المخصصة.',
      contact: 'تواصل معنا',
      call: 'اتصل بنا',
      whatsapp: 'واتساب',
      madeBy: 'صُنع بواسطة',
    },
    a11y: {
      openMenu: 'فتح القائمة',
      closeMenu: 'إغلاق القائمة',
      toggleTheme: 'تبديل وضع العرض',
      toggleLang: 'تبديل اللغة',
    },
    faq: {
      kicker: 'الأسئلة الشائعة',
      title: 'إجابات واضحة قبل أن تسأل',
      lead: 'إجابات مباشرة عن خدمات B-Code، أسلوب عملنا، وكيف نبدأ معاً.',
    },
    notFound: {
      title: 'الصفحة غير موجودة',
      lead: 'هذا الرابط غير متاح. عد إلى الصفحة الرئيسية لاستكشاف الخدمات والأعمال والأسعار.',
      home: 'العودة للرئيسية',
    },
  },
};
